import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from "@vercel/analytics/react";
import HomePage from './components/HomePage';
import ArchiveIndexPage from './components/ArchiveIndexPage';
import PuzzlePage from './components/PuzzlePage';
import CluePage from './components/CluePage';
import ArchivePage from './components/ArchivePage';
import RedditGamesPage from './components/RedditGamesPage';
import SearchPage from './components/SearchPage';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import SylloPage from './components/SylloPage';
import SylloCluePage from './components/SylloCluePage';
import SylloArchivePage from './components/SylloArchivePage';
import HotAndColdPage from './components/HotAndColdPage';
import HotAndColdArchivePage from './components/HotAndColdArchivePage';
import PocketGridsLandingPage from './components/PocketGridsLandingPage';
import SylloLandingPage from './components/SylloLandingPage';
import HotAndColdLandingPage from './components/HotAndColdLandingPage';
import ByrdleLandingPage from './components/ByrdleLandingPage';
import ByrdlePage from './components/ByrdlePage';
import ByrdleArchivePage from './components/ByrdleArchivePage';
import DailyGuessSolverPage from './components/DailyGuessSolverPage';
import AboutPage from './components/AboutPage';
import NotFoundPage from './components/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import { usePuzzles } from './context/PuzzleContext';
import { useSyllo } from './context/SylloContext';
import { useHotAndCold } from './context/HotAndColdContext';
import { useByrdle } from './context/ByrdleContext';

function App() {
  const { puzzles, loading } = usePuzzles();
  const { puzzles: sylloPuzzles, loading: sylloLoading } = useSyllo();
  const { puzzles: hotAndColdPuzzles, loading: hotAndColdLoading } = useHotAndCold();
  const { puzzles: byrdlePuzzles, loading: byrdleLoading } = useByrdle();

  if (loading || sylloLoading || hotAndColdLoading || byrdleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  // Get latest puzzle date safely
  const latestPuzzleDate = puzzles && puzzles.length > 0 ? puzzles[0].date : null;
  const latestSylloDate = sylloPuzzles && sylloPuzzles.length > 0 ? sylloPuzzles[0].date : null;
  const latestHotAndColdDate = hotAndColdPuzzles && hotAndColdPuzzles.length > 0 ? hotAndColdPuzzles[0].date : null;
  const latestByrdleDate = byrdlePuzzles && byrdlePuzzles.length > 0 ? byrdlePuzzles[0].date : null;

  if (!latestPuzzleDate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa] text-gray-500">
        No puzzles found.
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <Routes>
          {/* Root renders the new Home Page */}
          <Route path="/" element={<HomePage />} />
          
          {/* Game Landing Pages */}
          <Route path="/pocket-grids" element={<PocketGridsLandingPage />} />
          <Route path="/syllo" element={<SylloLandingPage />} />
          <Route path="/hot-and-cold" element={<HotAndColdLandingPage />} />
          <Route path="/byrdle" element={<ByrdleLandingPage />} />
          
          {/* Archive Index */}
          <Route path="/archives" element={<ArchiveIndexPage />} />

          {/* Archive Page */}
          <Route path="/archive" element={<ArchivePage />} />

          {/* Reddit Games Directory */}
          <Route path="/reddit-games" element={<RedditGamesPage />} />
          
          {/* Search Page */}
          <Route path="/search" element={<SearchPage />} />

          {/* Daily Guess Solver */}
          <Route path="/daily-guess-solver" element={<DailyGuessSolverPage />} />

          {/* About Page */}
          <Route path="/about" element={<AboutPage />} />

          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* SEO Friendly URL for Today's Puzzle */}
          <Route path="/todays-pocket-grids-clues-answers" element={<PuzzlePage isToday={true} date={latestPuzzleDate} />} />

          {/* Individual Clue Page for Today: /todays-pocket-grids-clues-answers/:slug */}
          <Route path="/todays-pocket-grids-clues-answers/:slug" element={<CluePage isToday={true} date={latestPuzzleDate} />} />

          {/* Syllo Routes - specific routes first */}
          <Route path="/todays-syllo-clues-answers" element={<SylloPage isToday={true} date={latestSylloDate} />} />
          <Route path="/todays-syllo-clues-answers/:slug" element={<SylloCluePage isToday={true} date={latestSylloDate} />} />
          <Route path="/syllo-archive" element={<SylloArchivePage />} />
          <Route path="/syllo/:puzzleId" element={<SylloPage isToday={false} />} />
          <Route path="/syllo/:puzzleId/:slug" element={<SylloCluePage isToday={false} />} />

          {/* Hot and Cold Routes */}
          <Route path="/todays-hot-and-cold-clues-answers" element={<HotAndColdPage isToday={true} date={latestHotAndColdDate} />} />
          <Route path="/hot-and-cold-archive" element={<HotAndColdArchivePage />} />
          <Route path="/hot-and-cold/:puzzleId" element={<HotAndColdPage isToday={false} />} />

          {/* Byrdle Routes */}
          <Route path="/todays-byrdle-clues-answers" element={<ByrdlePage isToday={true} date={latestByrdleDate} />} />
          <Route path="/byrdle-archive" element={<ByrdleArchivePage />} />
          <Route path="/byrdle/:puzzleId" element={<ByrdlePage isToday={false} />} />
          
          {/* Archive Puzzle Page: Matches /pocket-grid-2023-01-01 */}
          <Route path="/:puzzleId" element={<PuzzlePage isToday={false} />} />
          
          {/* Individual Clue Page for Archive: /pocket-grid-2023-01-01/clue-slug */}
          <Route path="/:puzzleId/:slug" element={<CluePage isToday={false} />} />
          
          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
